/**
 * FML Benefit Definitions
 *
 * Defines the structure, labels, and cell specs for each FML benefit field.
 * Uses shared cell specs and builder patterns for type/amount pairs.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  extendEnumWithNotDisclosed,
  NotDisclosed,
  RateGuaranteeTypeEnum,
  RfpFlmBenefitTypeType,
  RfpInclusionStatusEnum,
} from "@platform/openapi-types";

import { BenefitDefinitions } from "../../benefits/benefit.types";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildCommissionBenefits,
  buildTypeAmountBenefitPair,
} from "../shared/definitions/builders";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  // Current Carrier (disabled if policy period = new or alternate)
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },

  // Eligibility Classes (benefit plan group level)
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },

  // ========================================
  // COMMISSIONS
  // ========================================

  ...commissionDefs,
  // Override: RFP requires commission type fields to be required
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },

  // Federal FMLA - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "federalFmla",
    typeLabel: "Federal FMLA",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpFlmBenefitTypeType),
    amountCell: sharedCellSpecs.dollarAmountWithDecimals,
    amountLabel: "Amount",
    typeDescription:
      "Indicates if federal leaves are included in the provided plan",
    amountDescription: "Federal FMLA dollar amount",
    amountDefaultValue: 0,
    disabledValues: ["notIncluded", "includedInBasePrice", NotDisclosed],
  }),

  // Unpaid State Leaves - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "unpaidStateLeaves",
    typeLabel: "Unpaid State Leaves",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpFlmBenefitTypeType),
    amountCell: sharedCellSpecs.dollarAmountWithDecimals,
    typeDescription:
      "Indicates if additional unpaid state leaves are included in the provided plan",
    amountDescription: "Unpaid state leaves dollar amount",
    amountDefaultValue: 0,
    disabledValues: ["notIncluded", "includedInBasePrice", NotDisclosed],
  }),

  // Military (USERRA) - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "militaryUserra",
    typeLabel: "Military (USERRA)",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpFlmBenefitTypeType),
    amountCell: sharedCellSpecs.dollarAmountWithDecimals,
    typeDescription:
      "Indicates if military leaves are included in the provided plan",
    amountDescription: "Military (USERRA) dollar amount",
    amountDefaultValue: 0,
    disabledValues: ["notIncluded", "includedInBasePrice", NotDisclosed],
  }),

  // Jury Duty - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "juryDuty",
    typeLabel: "Jury Duty",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpFlmBenefitTypeType),
    amountCell: sharedCellSpecs.dollarAmountWithDecimals,
    typeDescription: "Indicates if jury duty is included in the provided plan",
    amountDescription: "Jury duty dollar amount",
    amountDefaultValue: 0,
    disabledValues: ["notIncluded", "includedInBasePrice", NotDisclosed],
  }),

  // ADA - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "ada",
    typeLabel: "ADA",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpFlmBenefitTypeType),
    amountCell: sharedCellSpecs.dollarAmountWithDecimals,
    typeDescription:
      "Indicates if ADA is included in the base price or for additional charge",
    amountDescription: "ADA dollar amount",
    amountDefaultValue: 0,
    disabledValues: ["notIncluded", "includedInBasePrice", NotDisclosed],
  }),

  // History and Takeover - text area with 4096 max length
  historyAndTakeover: {
    label: "History and Takeover",
    description:
      "Indicates if history and takeover is included in the new policy or if it will be clean slate takeover",
    cell: {
      ...sharedCellSpecs.textArea,
      cellEditorParams: {
        maxLength: 4096,
      },
    },
    networkDependent: false,
    defaultValue: "",
  },

  // Company Leaves - text area with 4096 max length
  companyLeaves: {
    label: "Company Leaves",
    description: "Indicates if additional company leaves can be supported",
    cell: {
      ...sharedCellSpecs.textArea,
      cellEditorParams: {
        maxLength: 4096,
      },
    },
    networkDependent: false,
    defaultValue: "",
  },

  // Correspondence - text area with 4096 max length
  correspondence: {
    label: "Correspondence",
    description: "Indicates how the carrier communicates with employees",
    cell: {
      ...sharedCellSpecs.textArea,
      cellEditorParams: {
        maxLength: 4096,
      },
    },
    networkDependent: false,
    defaultValue: "",
  },

  // Integrated STD/FMLA Claim Intake - enum dropdown
  integratedStdFmlaClaimIntake: {
    label: "Integrated STD/FMLA Claim Intake",
    description: "Confirms if the plan is integrated with disability",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },

  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },

  // Rate Guarantee - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
};
